import { useState, useEffect, useContext } from 'react';
import { ApiContext } from '../../App';
import { toast } from 'react-toastify';

const CompanyManagement = () => {
  const API = useContext(ApiContext);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newlinkedinUrl, setNewlinkedinUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('create'); // 'create', 'view', 'assign'

  // Charger les entreprises et utilisateurs au chargement
  useEffect(() => {
    fetchCompanies();
    fetchUsers();
  }, []);

  // Filtrer les utilisateurs lorsque searchTerm change
  useEffect(() => {
    if (searchTerm) {
      const filtered = users.filter(user => 
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers([]);
    }
  }, [searchTerm, users]);

  // Récupérer les entreprises
  const fetchCompanies = async () => {
    try {
      const response = await API.get('/api/companies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Erreur lors du chargement des entreprises');
    }
  };

  // Récupérer les utilisateurs
  const fetchUsers = async () => {
    try {
      const response = await API.get('/api/user/all');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Créer une nouvelle entreprise
  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!newCompanyName.trim()) {
      toast.error('Le nom de l\'entreprise est requis');
      return;
    }

    setLoading(true);
    try {
      const response = await API.post('/api/companies', {
        name: newCompanyName,
        linkedinUrl: newlinkedinUrl
      });

      setCompanies([...companies, response.data]);
      setNewCompanyName('');
      setNewlinkedinUrl('');
      toast.success('Entreprise créée avec succès');
    } catch (error) {
      console.error('Error creating company:', error);
      toast.error('Erreur lors de la création de l\'entreprise');
    } finally {
      setLoading(false);
    }
  };

  // Associer un utilisateur à une entreprise
  const handleAssignCompany = async (userId, companyId) => {
    try {
      await API.put(`/api/user/${userId}/company`, { companyId });
      
      // Mettre à jour l'état local des utilisateurs
      setUsers(users.map(user => 
        user._id === userId ? { ...user, companyId } : user
      ));
      
      toast.success('Utilisateur associé à l\'entreprise avec succès');
    } catch (error) {
      console.error('Error assigning company:', error);
      toast.error('Erreur lors de l\'association de l\'utilisateur à l\'entreprise');
    }
  };

  // Retirer un utilisateur d'une entreprise
  const handleRemoveFromCompany = async (userId) => {
    try {
      await API.delete(`/api/user/${userId}/company`);
      
      // Mettre à jour l'état local des utilisateurs
      setUsers(users.map(user => 
        user._id === userId ? { ...user, companyId: null } : user
      ));
      
      toast.success('Utilisateur retiré de l\'entreprise avec succès');
    } catch (error) {
      console.error('Error removing from company:', error);
      toast.error('Erreur lors du retrait de l\'utilisateur de l\'entreprise');
    }
  };

  // Obtenir le nom de l'entreprise à partir de l'ID
  const getCompanyName = (companyId) => {
    const company = companies.find(c => c._id === companyId);
    return company ? company.name : 'Non associé';
  };

  // Afficher les utilisateurs de l'entreprise sélectionnée
  const getUsersByCompany = (companyId) => {
    return users.filter(user => user.companyId === companyId);
  };

  const TabButton = ({ id, icon, label, isActive }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`flex items-center space-x-2 px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
        isActive
          ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md'
          : 'bg-white text-slate-600 hover:bg-gray-50 hover:text-slate-800 border border-gray-200'
      }`}
    >
      <i className={`bi ${icon} text-sm`}></i>
      <span className="text-sm">{label}</span>
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Navigation par onglets */}
      <div className="flex flex-wrap gap-3">
        <TabButton 
          id="create" 
          icon="bi-plus-circle" 
          label="Créer" 
          isActive={activeTab === 'create'} 
        />
        <TabButton 
          id="view" 
          icon="bi-eye" 
          label="Consulter" 
          isActive={activeTab === 'view'} 
        />
        <TabButton 
          id="assign" 
          icon="bi-person-plus" 
          label="Associer" 
          isActive={activeTab === 'assign'} 
        />
      </div>

      {/* Onglet : Créer une entreprise */}
      {activeTab === 'create' && (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <i className="bi bi-building-add text-white"></i>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Nouvelle Entreprise</h3>
              <p className="text-sm text-slate-600">Ajoutez une nouvelle entreprise au système</p>
            </div>
          </div>
          
          <form onSubmit={handleCreateCompany} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  <i className="bi bi-building mr-2 text-blue-600"></i>
                  Nom de l'entreprise
                </label>
                <input
                  type="text"
                  value={newCompanyName}
                  onChange={(e) => setNewCompanyName(e.target.value)}
                  className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-blue-400 focus:ring-4 focus:ring-blue-100 transition-all duration-200 text-sm placeholder-slate-400"
                  placeholder="Ex: Tech Solutions Inc."
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  <i className="bi bi-globe mr-2 text-green-600"></i>
                  Domaine web
                </label>
                <input
                  type="text"
                  value={newlinkedinUrl}
                  onChange={(e) => setNewlinkedinUrl(e.target.value)}
                  className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-blue-400 focus:ring-4 focus:ring-blue-100 transition-all duration-200 text-sm placeholder-slate-400"
                  placeholder="techsolutions.com"
                />
              </div>
            </div>
            
            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:ring-4 focus:ring-blue-100 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              >
                {loading ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    <span>Création...</span>
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle"></i>
                    <span>Créer l'entreprise</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Onglet : Consulter les entreprises */}
      {activeTab === 'view' && (
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <i className="bi bi-buildings text-white"></i>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Consulter les Entreprises</h3>
              <p className="text-sm text-slate-600">Visualisez les utilisateurs par entreprise</p>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-700 mb-3">
              <i className="bi bi-search mr-2 text-purple-600"></i>
              Sélectionner une entreprise
            </label>
            <div className="relative">
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                className="w-full max-w-md px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:ring-4 focus:ring-purple-100 transition-all duration-200 text-sm appearance-none"
              >
                <option value="">🏢 Choisir une entreprise</option>
                {companies.map(company => (
                  <option key={company._id} value={company._id}>
                    {company.name}
                  </option>
                ))}
              </select>
              <i className="bi bi-chevron-down absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            </div>
          </div>

          {selectedCompany && (
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-slate-800">
                  👥 {getCompanyName(selectedCompany)}
                </h4>
                <span className="bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                  {getUsersByCompany(selectedCompany).length} utilisateur{getUsersByCompany(selectedCompany).length > 1 ? 's' : ''}
                </span>
              </div>

              {/* Vue mobile - cartes */}
              <div className="block sm:hidden space-y-3">
                {getUsersByCompany(selectedCompany).map(user => (
                  <div key={user._id} className="bg-gradient-to-r from-white to-gray-50 p-4 rounded-xl border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900 text-sm">{user.name}</p>
                          <p className="text-xs text-slate-500">{user.email}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveFromCompany(user._id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                      >
                        <i className="bi bi-trash text-sm"></i>
                      </button>
                    </div>
                    <div className="bg-white p-2 rounded-lg">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold
                        ${user.role === 'admin' ? 'bg-green-100 text-green-800' : 
                          user.role === 'manager' ? 'bg-blue-100 text-blue-800' : 
                          'bg-gray-100 text-gray-800'}`}>
                        <i className={`bi ${
                          user.role === 'admin' ? 'bi-shield-fill' : 
                          user.role === 'manager' ? 'bi-person-gear' : 
                          'bi-person'
                        } mr-1`}></i>
                        {user.role}
                      </span>
                    </div>
                  </div>
                ))}
                {getUsersByCompany(selectedCompany).length === 0 && (
                  <div className="text-center py-8">
                    <div className="h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="bi bi-person-x text-xl text-gray-400"></i>
                    </div>
                    <p className="text-slate-500 font-medium">Aucun utilisateur</p>
                    <p className="text-xs text-slate-400">Cette entreprise n'a pas encore d'utilisateurs associés</p>
                  </div>
                )}
              </div>

              {/* Vue desktop - tableau */}
              <div className="hidden sm:block overflow-hidden rounded-xl border border-gray-200">
                <table className="min-w-full">
                  <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
                        <div className="flex items-center space-x-2">
                          <i className="bi bi-person text-slate-400"></i>
                          <span>Utilisateur</span>
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
                        <div className="flex items-center space-x-2">
                          <i className="bi bi-envelope text-slate-400"></i>
                          <span>Email</span>
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
                        <div className="flex items-center space-x-2">
                          <i className="bi bi-shield text-slate-400"></i>
                          <span>Rôle</span>
                        </div>
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase">
                        <div className="flex items-center justify-end space-x-2">
                          <i className="bi bi-gear text-slate-400"></i>
                          <span>Actions</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {getUsersByCompany(selectedCompany).map(user => (
                      <tr key={user._id} className="hover:bg-gradient-to-r hover:from-purple-50 hover:to-indigo-50 transition-all duration-200">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <div className="h-9 w-9 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
                              <span className="text-white font-semibold text-sm">
                                {user.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span className="text-sm font-medium text-slate-900">{user.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold
                            ${user.role === 'admin' ? 'bg-green-100 text-green-800' : 
                              user.role === 'manager' ? 'bg-blue-100 text-blue-800' : 
                              'bg-gray-100 text-gray-800'}`}>
                            <i className={`bi ${
                              user.role === 'admin' ? 'bi-shield-fill' : 
                              user.role === 'manager' ? 'bi-person-gear' : 
                              'bi-person'
                            } mr-1`}></i>
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={() => handleRemoveFromCompany(user._id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                    {getUsersByCompany(selectedCompany).length === 0 && (
                      <tr>
                        <td colSpan="4" className="px-6 py-12 text-center">
                          <div className="h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <i className="bi bi-person-x text-xl text-gray-400"></i>
                          </div>
                          <p className="text-slate-500 font-medium">Aucun utilisateur</p>
                          <p className="text-xs text-slate-400">Cette entreprise n'a pas encore d'utilisateurs associés</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Onglet : Associer des utilisateurs */}
      {activeTab === 'assign' && (
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="h-10 w-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <i className="bi bi-person-plus-fill text-white"></i>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Associer des Utilisateurs</h3>
              <p className="text-sm text-slate-600">Recherchez et associez des utilisateurs aux entreprises</p>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-700 mb-3">
              <i className="bi bi-search mr-2 text-green-600"></i>
              Rechercher un utilisateur
            </label>
            <div className="relative max-w-md">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-green-400 focus:ring-4 focus:ring-green-100 transition-all duration-200 text-sm placeholder-slate-400"
                placeholder="Nom ou email de l'utilisateur"
              />
              <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            </div>
          </div>

          {filteredUsers.length > 0 && (
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-slate-800">
                  🔍 Résultats de recherche
                </h4>
                <span className="bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  {filteredUsers.length} trouvé{filteredUsers.length > 1 ? 's' : ''}
                </span>
              </div>

              {/* Vue mobile - cartes */}
              <div className="block sm:hidden space-y-3">
                {filteredUsers.map(user => (
                  <div key={user._id} className="bg-gradient-to-r from-white to-gray-50 p-4 rounded-xl border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900 text-sm">{user.name}</p>
                          <p className="text-xs text-slate-500">{user.email}</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-white p-2 rounded-lg">
                        <span className="text-xs text-slate-500 block mb-1">Entreprise actuelle:</span>
                        <span className="text-sm font-medium text-slate-800">{getCompanyName(user.companyId)}</span>
                      </div>
                      <div className="flex space-x-2">
                        <select
                          className="flex-1 px-3 py-2 text-sm bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg focus:border-green-400"
                          value=""
                          onChange={(e) => handleAssignCompany(user._id, e.target.value)}
                        >
                          <option value="" disabled>🏢 Assigner à...</option>
                          {companies.map(company => (
                            <option key={company._id} value={company._id}>
                              {company.name}
                            </option>
                          ))}
                        </select>
                        {user.companyId && (
                          <button
                            onClick={() => handleRemoveFromCompany(user._id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                          >
                            <i className="bi bi-x-circle text-sm"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Vue desktop - tableau */}
              <div className="hidden sm:block overflow-hidden rounded-xl border border-gray-200">
                <table className="min-w-full">
                  <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
                        <div className="flex items-center space-x-2">
                          <i className="bi bi-person text-slate-400"></i>
                          <span>Utilisateur</span>
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
                        <div className="flex items-center space-x-2">
                          <i className="bi bi-envelope text-slate-400"></i>
                          <span>Email</span>
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
                        <div className="flex items-center space-x-2">
                          <i className="bi bi-building text-slate-400"></i>
                          <span>Entreprise actuelle</span>
                        </div>
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase">
                        <div className="flex items-center justify-end space-x-2">
                          <i className="bi bi-gear text-slate-400"></i>
                          <span>Actions</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {filteredUsers.map(user => (
                      <tr key={user._id} className="hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 transition-all duration-200">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <div className="h-9 w-9 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                              <span className="text-white font-semibold text-sm">
                                {user.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span className="text-sm font-medium text-slate-900">{user.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {getCompanyName(user.companyId)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex justify-end items-center space-x-2">
                            <select
                              className="px-3 py-1.5 text-sm bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg focus:border-green-400"
                              value=""
                              onChange={(e) => handleAssignCompany(user._id, e.target.value)}
                            >
                              <option value="" disabled>🏢 Assigner</option>
                              {companies.map(company => (
                                <option key={company._id} value={company._id}>
                                  {company.name}
                                </option>
                              ))}
                            </select>
                            {user.companyId && (
                              <button
                                onClick={() => handleRemoveFromCompany(user._id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 rounded-lg transition-colors"
                              >
                                <i className="bi bi-x-circle"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {searchTerm && filteredUsers.length === 0 && (
            <div className="bg-white rounded-xl p-8 border border-gray-200 text-center">
              <div className="h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="bi bi-search text-2xl text-gray-400"></i>
              </div>
              <p className="text-slate-500 font-medium">Aucun utilisateur trouvé</p>
              <p className="text-xs text-slate-400 mt-1">Essayez avec d'autres termes de recherche</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanyManagement;